import { createFileRoute, useParams } from '@tanstack/react-router';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { HStack, VStack } from '@/components/Layout';
import { ViewportLoader } from '@/components/Loading';
import useDocumentTitle from '@/hooks/useDocumentTitle';
import { defaultNS, useTranslation } from '@/lib/i18n';

import { useGetLicense } from '../../-query';

import CardFormMasonryGridGroup from './-components/CardFormMasonryGridGroup';
import TableOfContent from './-components/TableOfContent';
import { useLicenseRulesQuery } from './-query';

export const Route = createFileRoute(
  '/_staff/licenses/$id/_profile/rule-and-policy/',
)({
  component: LicenseRuleAndPolicy,
});

function LicenseRuleAndPolicy() {
  const { id: licenseId } = useParams({
    from: '/_staff/licenses/$id/_profile/rule-and-policy/',
  });
  const { data: licenseData } = useGetLicense(licenseId);
  const { data, isLoading, isError } = useLicenseRulesQuery(licenseId);
  const { t } = useTranslation(defaultNS, {
    keyPrefix: 'pages.profile.app.ruleAndPolicy',
  });

  useDocumentTitle(
    t('pageTitle', {
      name: licenseData.name,
      interpolation: { escapeValue: false },
    }),
  );

  const sectionRefs = useRef<{ [key: string]: React.RefObject<HTMLElement> }>(
    {},
  );
  const handleScrollToSection = useCallback((id: string) => {
    if (sectionRefs.current[id]?.current) {
      sectionRefs.current[id].current.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
      });
    }
  }, []);

  const subGroupRules = useMemo(
    () => data?.subGroupRules ?? [],
    [data?.subGroupRules],
  );
  const hasSubGroups = subGroupRules.length > 0;

  const rulesBySubGroup = useMemo(() => {
    if (!data || !hasSubGroups) return null;
    const map = new Map<string, (typeof data.rules)[number]>();
    for (const rule of data.rules) {
      if (rule.licenseSubGroupId) {
        map.set(rule.licenseSubGroupId, rule);
      }
    }
    return map;
  }, [data, hasSubGroups]);

  const [selectedTab, setSelectedTab] = useState<string>('');

  useEffect(() => {
    if (selectedTab || !data) return;
    if (hasSubGroups && subGroupRules[0]) {
      // oxlint-disable-next-line react-hooks-js/set-state-in-effect -- TODO: eslint-plugin-react-hooks v7 の React Compiler ルールに対応
      setSelectedTab(subGroupRules[0].subGroupId);
    } else if (data.rules[0]?.peopleGroupId) {
      setSelectedTab(data.rules[0].peopleGroupId);
    }
  }, [data, selectedTab, hasSubGroups, subGroupRules]);

  const handleSelectedTab = useCallback(
    (id: string) => {
      setSelectedTab(id);
      handleScrollToSection(id);
    },
    [handleScrollToSection],
  );

  if (isLoading) {
    return <ViewportLoader />;
  }

  if (isError || !data) {
    return null;
  }

  return (
    <HStack className="flex gap-9 p-10">
      <VStack className="w-[280px] shrink-0 gap-6">
        <TableOfContent
          rules={data.rules}
          subGroupRules={subGroupRules}
          selectedTab={selectedTab}
          handleSelectedTab={handleSelectedTab}
        />
      </VStack>

      <VStack className="gap-5 w-full">
        {hasSubGroups
          ? subGroupRules.map((subGroup) => {
              const rule = rulesBySubGroup?.get(subGroup.subGroupId);
              if (!rule) return null;
              return (
                <CardFormMasonryGridGroup
                  key={subGroup.subGroupId}
                  rule={rule}
                  subGroupRule={subGroup}
                  sectionKey={subGroup.subGroupId}
                  sectionRefs={sectionRefs}
                />
              );
            })
          : data.rules.map((rule) => (
              <CardFormMasonryGridGroup
                key={rule.peopleGroupId}
                rule={rule}
                sectionKey={rule.peopleGroupId}
                sectionRefs={sectionRefs}
              />
            ))}
      </VStack>
    </HStack>
  );
}
